/** @format */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	AcceptAddRequest,
	AcceptJoinRequest,
	AllCirclesContributionResponse,
	CircleData,
	ContributionsChartResponse,
	IAccountVerification,
	IAccountVerificationPayload,
	ICircleExpectedTotalPayOut,
	ICircleInfo,
	ICircleLeaderApplication,
	ICirclePayload,
	ICircleStripeConnectedAccountBalance,
	ICreateCircle,
	IUnionCycleBalances,
	IUserAddRequest,
	JoinRequestInfo,
	JoinRequestOfCircleByCircleLeaderIdResult,
	JoinRequestsApplications,
	JoinRequestsOfCircleResult,
	JoinRequestStatus,
	JoinRequestUserSent,
	LeaderSendAddRequestInfo,
	AddRequestsApplications,
	AddRequestStatus,
} from "../../utility/types/circleType";
import {
	circleLeaderApplication,
	accountVerification,
	createCircle,
	denyAddRequest,
	fetchAllCircles,
	fetchCircle,
	fetchCircleByLeader,
	fetchLeaderSendAddRequest,
	fetchViewAndSearchAddRequestSendOut,
	updateCircle,
	acceptAddRequest,
	fetchCircleById,
	getAccountVerificationStatus,
	joinCircleRequest,
	fetchJoinCircleRequestsOfCircle,
	fetchJoinCircleRequestsOfCircleByCircleLeaderId,
	acceptJoinRequest,
	denyJoinRequest,
	getCircleConnectedAccountBalance,
	getCircleExpectedTotalPayOut,
	fetchJoinRequestUserSent,
	getUnionOrCircleCycleBalances,
	fetchAllCirclesCurrentMonthContribution,
	fetchAllCirclesContributionHistory,
	fetchPublicStats,
	fetchContributionsChartData,
	fetchAllUnionLeaderApplications,
	approveApplication,
	rejectApplication,
	deleteApplication,
	fetchAllCirclesAdmin,
	fetchCircleByIdAdmin,
	updateCircleByIdAdmin,
	deleteCircleByIdAdmin,
	banCircleAdmin,
	unbanCircleAdmin,
	fetchAllJoinRequestsAsAdmin,
	approveJoinRequest,
	rejectJoinRequest,
	fetchAllAddRequestsAsAdmin,
	approveAddRequest,
	rejectAddRequest,
	fetchCirclePayoutOrder,
} from "../actions/circleAction";
import { SearchUserAddRequestResult } from "../../utility/types/addRequestType";

export interface CircleState {
	circleDataPayload: ICirclePayload | null;
	circleData: ICreateCircle[];
	adminCircles?: ICircleInfo[];
	circleInfo: ICircleInfo[];
	circleDataAccess: CircleData | null;
	circleLeaderApplication: ICircleLeaderApplication | null;
	memberAddRequest: IUserAddRequest[] | null;
	memberAcceptedAddRequest: AcceptAddRequest[] | null;
	leaderAcceptedUserJoinRequest: AcceptJoinRequest[] | null;
	leaderSendAddRequestInfo: LeaderSendAddRequestInfo | null;
	joinRequestInfo: JoinRequestInfo | null;
	joinRequestsOfCircleResult: JoinRequestsOfCircleResult | null;
	joinRequestOfCircleByCircleLeaderIdResult: JoinRequestOfCircleByCircleLeaderIdResult | null;
	joinRequestUserSent: JoinRequestUserSent | null;
	viewAndSearchAddRequestData: SearchUserAddRequestResult | null;
	circleStripeConnectedAccountBalance: ICircleStripeConnectedAccountBalance | null;
	circleExpectedTotalPayOut: ICircleExpectedTotalPayOut | null;
	unionCycleBalances: IUnionCycleBalances | null;
	allCirclesCurrentMonthContribution: AllCirclesContributionResponse | null;
	allCirclesContributionHistory: AllCirclesContributionResponse | null;
	contributionsChartData: ContributionsChartResponse | null;
	unionLeaderApplications: any[];
	joinRequestsApplications: JoinRequestsApplications | null;
	isLoadingJoinRequests: boolean;
	isApprovingJoinRequest: boolean;
	isRejectingJoinRequest: boolean;
	addRequestsApplications: AddRequestsApplications | null;
	isLoadingAddRequests: boolean;
	isApprovingAddRequest: boolean;
	isRejectingAddRequest: boolean;
	addRequestError: string;
	isLoadingApplications: boolean;
	isApprovingApplication: boolean;
	isRejectingApplication: boolean;
	isDeletingApplication: boolean;
	applicationError: string;
	joinRequestError: string;
	isLoading: boolean;
	joinRequestSuccessMessage: string;
	joinRequestErrorMessage: string;
	error: string;
	circlePayoutOrder: any | null;
	isLoadingPayoutOrder: boolean;
	payoutOrderError: string;
}

export interface AccountVerificationState {
	accountVerificationPayload: IAccountVerificationPayload | null;
	accountVerificationData: IAccountVerification | null;
	isLoading: boolean;
	error: string;
}

const initialState: CircleState = {
	circleDataPayload: null,
	circleData: [],
	adminCircles: [],
	circleInfo: [],
	circleDataAccess: null,
	circleLeaderApplication: null,
	memberAddRequest: null,
	memberAcceptedAddRequest: null,
	leaderAcceptedUserJoinRequest: null,
	leaderSendAddRequestInfo: null,
	joinRequestInfo: null,
	joinRequestsOfCircleResult: null,
	joinRequestOfCircleByCircleLeaderIdResult: null,
	joinRequestUserSent: null,
	viewAndSearchAddRequestData: null,
	circleStripeConnectedAccountBalance: null,
	circleExpectedTotalPayOut: null,
	unionCycleBalances: null,
	allCirclesCurrentMonthContribution: null,
	allCirclesContributionHistory: null,
	contributionsChartData: null,
	unionLeaderApplications: [],
	joinRequestsApplications: null,
	isLoadingJoinRequests: false,
	isApprovingJoinRequest: false,
	isRejectingJoinRequest: false,
	addRequestsApplications: null,
	isLoadingAddRequests: false,
	isApprovingAddRequest: false,
	isRejectingAddRequest: false,
	addRequestError: "",
	isLoadingApplications: false,
	isApprovingApplication: false,
	isRejectingApplication: false,
	isDeletingApplication: false,
	applicationError: "",
	joinRequestError: "",
	isLoading: false,
	joinRequestSuccessMessage: "",
	joinRequestErrorMessage: "",
	error: "",
	circlePayoutOrder: null,
	isLoadingPayoutOrder: false,
	payoutOrderError: "",
};

const initialAccountVerificationState: AccountVerificationState = {
	accountVerificationData: null,
	accountVerificationPayload: null,
	isLoading: false,
	error: "",
};

export const accountVerificationSlice = createSlice({
	name: "accountVerification",
	initialState: initialAccountVerificationState,
	reducers: {
		setAccountVerificationData: (
			state,
			action: PayloadAction<IAccountVerificationPayload>,
		) => {
			state.accountVerificationPayload = action.payload;
		},
	},

	extraReducers: (builder) => {
		builder.addCase(
			getAccountVerificationStatus.pending,
			(state, action: PayloadAction<any>) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			getAccountVerificationStatus.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.accountVerificationData = action.payload;
			},
		);

		builder.addCase(
			getAccountVerificationStatus.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to send  getVerificationStatus request. Unknown error occurred";
			},
		);
	},
});

export const circleSlice = createSlice({
	name: "circle",
	initialState,
	reducers: {
		setCircleData: (state, action: PayloadAction<ICirclePayload>) => {
			state.circleDataPayload = action.payload;
		},
		clearJoinMessage(state) {
			state.joinRequestSuccessMessage = "";
			state.joinRequestErrorMessage = "";
		},
		clearUnionCycleBalances(state) {
			state.unionCycleBalances = null;
		},
	},

	extraReducers: (builder) => {
		builder.addCase(
			circleLeaderApplication.pending,
			(state, action: PayloadAction<any>) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			circleLeaderApplication.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.circleLeaderApplication = action.payload;
			},
		);

		builder.addCase(
			circleLeaderApplication.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to send application. Unknown error occurred";
			},
		);

		builder.addCase(
			accountVerification.pending,
			(state, action: PayloadAction<any>) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			accountVerification.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.circleDataPayload = action.payload;
			},
		);

		builder.addCase(
			accountVerification.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to send verification. Unknown error occurred";
			},
		);

		builder.addCase(
			createCircle.pending,
			(state, action: PayloadAction<any>) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			createCircle.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;

				// if (!Array.isArray(state.circleInfo)) {
				//   state.circleInfo = [];
				// }
				state.circleInfo = [...state.circleInfo, action.payload];
				state.circleDataPayload = action.payload;
			},
		);

		builder.addCase(
			createCircle.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to send verification. Unknown error occurred";
			},
		);

		builder.addCase(
			fetchCircleByLeader.pending,
			(state, action: PayloadAction<any>) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			fetchCircleByLeader.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.circleInfo = action.payload;
			},
		);

		builder.addCase(
			fetchCircleByLeader.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload || "Failed to fetch circles. Unknown error occurred";
			},
		);

		builder.addCase(fetchCircleById.pending, (state, action) => {
			state.isLoading = true;
		});

		builder.addCase(fetchCircleById.fulfilled, (state, action) => {
			state.isLoading = false;
			state.circleDataAccess = action.payload;
		});

		builder.addCase(fetchCircleById.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload === "string" ?
					action.payload
					: "Failed get circle by ID. Unknown error occurred";
		});

		builder.addCase(
			fetchCircle.pending,
			(state, action: PayloadAction<any>) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			fetchCircle.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.circleInfo = action.payload;
			},
		);

		builder.addCase(
			fetchCircle.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to send verification. Unknown error occurred";
			},
		);

		builder.addCase(acceptAddRequest.pending, (state, action) => {
			state.isLoading = true;
		});

		builder.addCase(acceptAddRequest.fulfilled, (state, action) => {
			state.isLoading = false;
			// Assuming action.payload contains the updated user and union info
			const newUnionData = action.payload.union;

			const updatedUserData = action.payload.user;

			// update circle info(maybe): WHY ?

			state.circleInfo = state.circleInfo.map((info) => {
				if (info._id === newUnionData._id) {
					return {
						...info,
						...newUnionData,
					};
				}
				return info;
			});

			// in case circle is not to the state, lets add it
			if (!state.circleInfo.find((info) => info._id === newUnionData._id)) {
				state.circleInfo.push(newUnionData);
			}

			// update member accepted request with new data.
			state.memberAcceptedAddRequest =
				state.memberAcceptedAddRequest ?
					[...state.memberAcceptedAddRequest, action.payload]
					: [action.payload];

			// update user related states, like update list of circles the user is part of.
			if (updatedUserData && updatedUserData.unions) {
				state.circleInfo = updatedUserData.unions;
			}
		});

		builder.addCase(acceptAddRequest.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload === "string" ?
					action.payload
					: "Failed to accept request. Unknown error occurred";
		});

		builder.addCase(
			denyAddRequest.pending,
			(state, action: PayloadAction<any>) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			denyAddRequest.fulfilled,
			(state, action: PayloadAction<any, string, { arg: string }>) => {
				state.isLoading = false;
				const updatedRequest = action.payload;
				state.memberAddRequest =
					state?.memberAddRequest?.map((request) =>
						request._id === updatedRequest._id ? updatedRequest : request,
					) || [];
			},
		);

		builder.addCase(
			denyAddRequest.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload || "Failed to deny request. Unknown error occurred";
			},
		);

		builder.addCase(fetchAllCircles.pending, (state, action) => {
			state.isLoading = true;
		});

		builder.addCase(fetchAllCircles.fulfilled, (state, action) => {
			state.isLoading = false;
			// Handle both array and object responses (e.g., { circles: [...] })
			const payload = action.payload || {};
			const circlesArray =
				Array.isArray(payload) ? payload : payload.circles || [];
			state.circleInfo = circlesArray;
			state.circleData = circlesArray;
		});

		builder.addCase(fetchAllCircles.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload === "string" ?
					action.payload
					: "Failed to fetch all circles. Unknown error occurred";
		});

		// Admin: fetch all circles
		builder.addCase(fetchAllCirclesAdmin.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(fetchAllCirclesAdmin.fulfilled, (state, action) => {
			state.isLoading = false;
			// payload expected { total, circles, generatedAt }
			const payload = action.payload || {};
			state.circleInfo = payload.circles || payload || [];
			state.adminCircles = payload.circles || payload || [];
		});

		builder.addCase(fetchAllCirclesAdmin.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload === "string" ?
					action.payload
					: "Failed to fetch admin circles. Unknown error occurred";
		});

		// Admin: fetch circle by id
		builder.addCase(fetchCircleByIdAdmin.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			fetchCircleByIdAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				const payload = action.payload || {};
				const circle = payload.circle || payload;
				if (circle) {
					state.circleDataAccess = circle;
					// keep circleInfo in sync
					const idx = state.circleInfo.findIndex((c) => c._id === circle._id);
					if (idx !== -1) {
						state.circleInfo[idx] = { ...state.circleInfo[idx], ...circle };
					} else {
						state.circleInfo.push(circle);
					}

					// keep adminCircles in sync
					const idx2 =
						state.adminCircles?.findIndex((c) => c._id === circle._id) ?? -1;
					if (idx2 !== -1) {
						(state.adminCircles as ICircleInfo[])[idx2] = {
							...(state.adminCircles as ICircleInfo[])[idx2],
							...circle,
						};
					} else {
						(state.adminCircles as ICircleInfo[]).push(circle);
					}
				}
			},
		);

		builder.addCase(fetchCircleByIdAdmin.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload === "string" ?
					action.payload
					: "Failed to fetch admin circle. Unknown error occurred";
		});

		// Admin: update circle by id
		builder.addCase(updateCircleByIdAdmin.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			updateCircleByIdAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				const payload = action.payload || {};
				const updated = payload.updatedCircle || payload;
				if (updated) {
					// update list
					const idx = state.circleInfo.findIndex((c) => c._id === updated._id);
					if (idx !== -1) {
						state.circleInfo[idx] = { ...state.circleInfo[idx], ...updated };
					} else {
						state.circleInfo.push(updated);
					}

					const idx2 =
						state.adminCircles?.findIndex((c) => c._id === updated._id) ?? -1;
					if (idx2 !== -1) {
						(state.adminCircles as ICircleInfo[])[idx2] = {
							...(state.adminCircles as ICircleInfo[])[idx2],
							...updated,
						};
					} else {
						(state.adminCircles as ICircleInfo[]).push(updated);
					}

					// update detail if open
					if (
						state.circleDataAccess &&
						state.circleDataAccess._id === updated._id
					) {
						state.circleDataAccess = {
							...state.circleDataAccess,
							...updated,
						} as any;
					}
				}
			},
		);

		builder.addCase(updateCircleByIdAdmin.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload === "string" ?
					action.payload
					: "Failed to update circle (admin).";
		});

		// Admin: delete circle by id
		builder.addCase(deleteCircleByIdAdmin.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			deleteCircleByIdAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				const payload = action.payload || {};
				const deletedId = payload.deletedCircleId || payload._id || payload;
				if (deletedId) {
					state.circleInfo = state.circleInfo.filter(
						(c) => c._id !== deletedId,
					);
					state.adminCircles = (state.adminCircles || []).filter(
						(c) => c._id !== deletedId,
					);
					if (
						state.circleDataAccess &&
						state.circleDataAccess._id === deletedId
					) {
						state.circleDataAccess = null;
					}
				}
			},
		);

		builder.addCase(deleteCircleByIdAdmin.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload === "string" ?
					action.payload
					: "Failed to delete circle (admin).";
		});

		// Admin: ban circle
		builder.addCase(banCircleAdmin.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			banCircleAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				const payload = action.payload || {};
				const updated = payload.updatedCircle || payload.circle || payload;
				if (updated) {
					// update in lists
					state.circleInfo = state.circleInfo.map((c) =>
						c._id === updated._id ? { ...c, ...updated } : c,
					);
					state.adminCircles = (state.adminCircles || []).map((c) =>
						c._id === updated._id ? { ...c, ...updated } : c,
					);
					if (
						state.circleDataAccess &&
						state.circleDataAccess._id === updated._id
					) {
						state.circleDataAccess = {
							...state.circleDataAccess,
							...updated,
						} as any;
					}
				}
			},
		);

		builder.addCase(banCircleAdmin.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload === "string" ?
					action.payload
					: "Failed to ban circle (admin).";
		});

		// Admin: unban circle
		builder.addCase(unbanCircleAdmin.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			unbanCircleAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				const payload = action.payload || {};
				const updated = payload.updatedCircle || payload.circle || payload;
				if (updated) {
					state.circleInfo = state.circleInfo.map((c) =>
						c._id === updated._id ? { ...c, ...updated } : c,
					);
					state.adminCircles = (state.adminCircles || []).map((c) =>
						c._id === updated._id ? { ...c, ...updated } : c,
					);
					if (
						state.circleDataAccess &&
						state.circleDataAccess._id === updated._id
					) {
						state.circleDataAccess = {
							...state.circleDataAccess,
							...updated,
						} as any;
					}
				}
			},
		);

		builder.addCase(unbanCircleAdmin.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload === "string" ?
					action.payload
					: "Failed to unban circle (admin).";
		});

		builder.addCase(fetchLeaderSendAddRequest.pending, (state, action) => {
			state.isLoading = true;
		});

		builder.addCase(fetchLeaderSendAddRequest.fulfilled, (state, action) => {
			state.isLoading = false;
			state.leaderSendAddRequestInfo = action.payload;
		});

		builder.addCase(fetchLeaderSendAddRequest.rejected, (state, action) => {
			state.isLoading = false;
			state.error = action.error.message || "Failed to send add request";
		});

		builder.addCase(updateCircle.pending, (state, action) => {
			state.isLoading = true;
		});

		builder.addCase(updateCircle.fulfilled, (state, action) => {
			state.isLoading = false;
			const updatedCircleIndex = state.circleInfo.findIndex(
				(circle) => circle._id === action.payload._id,
			);
			if (updatedCircleIndex !== -1) {
				state.circleInfo[updatedCircleIndex] = action.payload;
			} else {
				// Make sure to push into the array, not replace the array
				state.circleInfo.push(action.payload);
			}
		});

		builder.addCase(updateCircle.rejected, (state, action) => {
			state.isLoading = false;
			state.error = action.error.message || "Failed to update circle request";
		});

		builder.addCase(
			fetchViewAndSearchAddRequestSendOut.pending,
			(state, action) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			fetchViewAndSearchAddRequestSendOut.fulfilled,
			(state, action) => {
				state.isLoading = false;
				state.viewAndSearchAddRequestData = action.payload;
			},
		);

		builder.addCase(
			fetchViewAndSearchAddRequestSendOut.rejected,
			(state, action) => {
				state.isLoading = false;
				state.error =
					typeof action.payload === "string" ?
						action.payload
						: "Failed to fetch data";
			},
		);

		builder.addCase(joinCircleRequest.pending, (state) => {
			state.isLoading = true;
			state.joinRequestSuccessMessage = "";
			state.joinRequestErrorMessage = "";
		});

		builder.addCase(joinCircleRequest.fulfilled, (state, action) => {
			console.log("action.payload", action.payload);
			state.isLoading = false;
			state.joinRequestInfo = action.payload;
			state.joinRequestSuccessMessage =
				action.payload.message || "Join Request sent successfully";
		});

		builder.addCase(joinCircleRequest.rejected, (state, action) => {
			state.isLoading = false;
			state.joinRequestErrorMessage =
				(action.payload as string) || "Failed to send join";
		});

		builder.addCase(fetchJoinCircleRequestsOfCircle.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			fetchJoinCircleRequestsOfCircle.fulfilled,
			(state, action) => {
				state.isLoading = false;
				state.joinRequestsOfCircleResult = action.payload;
			},
		);

		builder.addCase(
			fetchJoinCircleRequestsOfCircle.rejected,
			(state, action) => {
				state.isLoading = false;
				state.error =
					typeof action.payload === "string" ?
						action.payload
						: "Failed to fetch data";
			},
		);

		builder.addCase(
			fetchJoinCircleRequestsOfCircleByCircleLeaderId.pending,
			(state) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			fetchJoinCircleRequestsOfCircleByCircleLeaderId.fulfilled,
			(state, action) => {
				state.isLoading = false;
				state.joinRequestOfCircleByCircleLeaderIdResult = action.payload;
			},
		);

		builder.addCase(
			fetchJoinCircleRequestsOfCircleByCircleLeaderId.rejected,
			(state, action) => {
				state.isLoading = false;
				state.error =
					typeof action.payload === "string" ?
						action.payload
						: "Failed to fetch data";
			},
		);

		builder.addCase(fetchJoinRequestUserSent.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(fetchJoinRequestUserSent.fulfilled, (state, action) => {
			state.isLoading = false;
			state.joinRequestUserSent = action.payload;
		});

		builder.addCase(fetchJoinRequestUserSent.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload === "string" ?
					action.payload
					: "Failed to fetch data";
		});

		builder.addCase(acceptJoinRequest.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			acceptJoinRequest.fulfilled,
			(state, action: PayloadAction<AcceptJoinRequest>) => {
				state.isLoading = false;

				const {
					circle,
					// joinRequest
				} = action.payload;

				// making circle object to match ICircleInfo
				const transformCircle: ICircleInfo = {
					...circle,
					leader:
						typeof circle.leader === "string" ?
							{
								_id: circle.leader,
								email: "",
								firstName: "",
								lastName: "",
								profileImage: "",
							}
							: circle.leader,
				};

				// update circle information in state
				state.circleInfo = state.circleInfo.map((info) =>
					info._id === transformCircle._id ? transformCircle : info,
				);

				// if circle not already in state then we add it.
				if (
					!state.circleInfo.find((info) => info._id === transformCircle._id)
				) {
					state.circleInfo.push(transformCircle);
				}

				// update accepted join request
				state.leaderAcceptedUserJoinRequest =
					state.leaderAcceptedUserJoinRequest ?
						[...state.leaderAcceptedUserJoinRequest, action.payload]
						: [action.payload];

				state.joinRequestErrorMessage = ""; // clear any errors
			},
		);

		builder.addCase(acceptJoinRequest.rejected, (state, action) => {
			state.isLoading = false;
			state.joinRequestErrorMessage =
				typeof action.payload === "string" ?
					action.payload
					: "Failed to approve join request. Unknown error occurred";
		});

		builder.addCase(denyJoinRequest.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			denyJoinRequest.fulfilled,
			(state, action: PayloadAction<any, string>) => {
				state.isLoading = false;
				const updateRequest = action.payload;
				state.leaderAcceptedUserJoinRequest =
					state.leaderAcceptedUserJoinRequest?.map((request) =>
						request.joinRequest._id === updateRequest.joinRequest._id ?
							updateRequest
							: request,
					) || [];
			},
		);

		builder.addCase(
			denyJoinRequest.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload || "Failed to deny request. Unknown error occurred";
			},
		);

		builder.addCase(getCircleConnectedAccountBalance.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			getCircleConnectedAccountBalance.fulfilled,
			(state, action: PayloadAction<any, string>) => {
				state.isLoading = false;
				state.circleStripeConnectedAccountBalance = action.payload;
			},
		);

		builder.addCase(
			getCircleConnectedAccountBalance.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to get circle connected account balance. Unknown error occurred";
			},
		);

		builder.addCase(getCircleExpectedTotalPayOut.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			getCircleExpectedTotalPayOut.fulfilled,
			(state, action: PayloadAction<any, string>) => {
				state.isLoading = false;
				state.circleExpectedTotalPayOut = action.payload;
			},
		);

		builder.addCase(
			getCircleExpectedTotalPayOut.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to get circle connected account balance. Unknown error occurred";
			},
		);

		// New Union Cycle Balances handlers
		builder.addCase(getUnionOrCircleCycleBalances.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			getUnionOrCircleCycleBalances.fulfilled,
			(state, action: PayloadAction<IUnionCycleBalances>) => {
				state.isLoading = false;
				state.unionCycleBalances = action.payload;
			},
		);

		builder.addCase(
			getUnionOrCircleCycleBalances.rejected,
			(state) => {
				state.isLoading = false;
				// Intentionally not setting the shared `error`: it is rendered by
				// unrelated circle screens. The dashboard falls back to cycle totals.
				state.unionCycleBalances = null;
			},
		);

		builder.addCase(
			fetchAllCirclesCurrentMonthContribution.pending,
			(state) => {
				state.isLoading = true;
				state.error = "";
			},
		);

		builder.addCase(
			fetchAllCirclesCurrentMonthContribution.fulfilled,
			(state, action: PayloadAction<AllCirclesContributionResponse>) => {
				state.isLoading = false;
				state.allCirclesCurrentMonthContribution = action.payload;
			},
		);

		builder.addCase(
			fetchAllCirclesCurrentMonthContribution.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to fetch all circles current month contributions. Unknown error occurred";
			},
		);

		// All-time contribution history
		builder.addCase(fetchAllCirclesContributionHistory.pending, (state) => {
			state.isLoading = true;
			state.error = "";
		});

		builder.addCase(
			fetchAllCirclesContributionHistory.fulfilled,
			(state, action: PayloadAction<AllCirclesContributionResponse>) => {
				state.isLoading = false;
				state.allCirclesContributionHistory = action.payload;
			},
		);

		builder.addCase(
			fetchAllCirclesContributionHistory.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to fetch all-time contribution history. Unknown error occurred";
			},
		);

		// Public stats (no auth required)
		builder.addCase(fetchPublicStats.pending, (state) => {
			state.isLoading = true;
			state.error = "";
		});

		builder.addCase(
			fetchPublicStats.fulfilled,
			(state, action: PayloadAction<AllCirclesContributionResponse>) => {
				state.isLoading = false;
				state.allCirclesCurrentMonthContribution = action.payload;
			},
		);

		builder.addCase(
			fetchPublicStats.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to fetch public stats. Unknown error occurred";
			},
		);

		builder.addCase(fetchContributionsChartData.pending, (state) => {
			state.isLoading = true;
			state.error = "";
		});

		builder.addCase(
			fetchContributionsChartData.fulfilled,
			(state, action: PayloadAction<ContributionsChartResponse>) => {
				state.isLoading = false;
				state.contributionsChartData = action.payload;
			},
		);

		builder.addCase(
			fetchContributionsChartData.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to fetch contributions chart data. Unknown error occurred";
			},
		);

		// Union Leader Applications
		builder.addCase(fetchAllUnionLeaderApplications.pending, (state) => {
			state.isLoadingApplications = true;
			state.applicationError = "";
		});

		builder.addCase(
			fetchAllUnionLeaderApplications.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoadingApplications = false;
				// The API returns the array directly, not wrapped in an object
				state.unionLeaderApplications =
					Array.isArray(action.payload) ? action.payload : [];
			},
		);

		builder.addCase(
			fetchAllUnionLeaderApplications.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoadingApplications = false;
				state.applicationError =
					action.payload || "Failed to fetch union leader applications";
			},
		);

		// Approve Application
		builder.addCase(approveApplication.pending, (state) => {
			state.isApprovingApplication = true;
			state.applicationError = "";
		});

		builder.addCase(
			approveApplication.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isApprovingApplication = false;
				// Update the application in the list
				const updatedApp = action.payload?.updatedApplication;
				if (updatedApp) {
					state.unionLeaderApplications = state.unionLeaderApplications.map(
						(app) => (app._id === updatedApp._id ? updatedApp : app),
					);
				}
			},
		);
		builder.addCase(
			approveApplication.rejected,
			(state, action: PayloadAction<any>) => {
				state.isApprovingApplication = false;
				state.applicationError =
					action.payload || "Failed to approve application";
			},
		);

		// Reject Application
		builder.addCase(rejectApplication.pending, (state) => {
			state.isRejectingApplication = true;
			state.applicationError = "";
		});

		builder.addCase(
			rejectApplication.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isRejectingApplication = false;
				// Update the application in the list
				const updatedApp = action.payload?.updatedApplication;
				if (updatedApp) {
					state.unionLeaderApplications = state.unionLeaderApplications.map(
						(app) => (app._id === updatedApp._id ? updatedApp : app),
					);
				}
			},
		);

		builder.addCase(
			rejectApplication.rejected,
			(state, action: PayloadAction<any>) => {
				state.isRejectingApplication = false;
				state.applicationError =
					action.payload || "Failed to reject application";
			},
		);

		// Delete Application
		builder.addCase(deleteApplication.pending, (state) => {
			state.isDeletingApplication = true;
			state.applicationError = "";
		});

		builder.addCase(
			deleteApplication.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isDeletingApplication = false;
				// Remove the application from the list
				const deletedId = action.payload?.deletedApplicationId;
				if (deletedId) {
					state.unionLeaderApplications = state.unionLeaderApplications.filter(
						(app) => app._id !== deletedId,
					);
				}
			},
		);

		builder.addCase(
			deleteApplication.rejected,
			(state, action: PayloadAction<any>) => {
				state.isDeletingApplication = false;
				state.applicationError =
					action.payload || "Failed to delete application";
			},
		);

		// Fetch All Join Requests (Admin)
		builder.addCase(fetchAllJoinRequestsAsAdmin.pending, (state) => {
			state.isLoadingJoinRequests = true;
			state.joinRequestError = "";
		});

		builder.addCase(
			fetchAllJoinRequestsAsAdmin.fulfilled,
			(state, action: PayloadAction<JoinRequestsApplications>) => {
				state.isLoadingJoinRequests = false;
				state.joinRequestsApplications = action.payload;
			},
		);

		builder.addCase(
			fetchAllJoinRequestsAsAdmin.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoadingJoinRequests = false;
				state.joinRequestError =
					action.payload || "Failed to fetch join requests";
			},
		);

		// Approve Join Request (Admin)
		builder.addCase(approveJoinRequest.pending, (state) => {
			state.isApprovingJoinRequest = true;
			state.joinRequestError = "";
		});

		builder.addCase(
			approveJoinRequest.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isApprovingJoinRequest = false;
				// Update the join request in the list
				if (state.joinRequestsApplications?.joinRequests) {
					const requestId = action.payload?.joinRequest?._id;
					const requestIndex =
						state.joinRequestsApplications.joinRequests.findIndex(
							(req) => req._id === requestId,
						);
					if (requestIndex !== -1) {
						state.joinRequestsApplications.joinRequests[requestIndex] = {
							...state.joinRequestsApplications.joinRequests[requestIndex],
							status: JoinRequestStatus.Approved,
						};
					}
				}
			},
		);

		builder.addCase(
			approveJoinRequest.rejected,
			(state, action: PayloadAction<any>) => {
				state.isApprovingJoinRequest = false;
				state.joinRequestError =
					action.payload || "Failed to approve join request";
			},
		);

		// Reject Join Request (Admin)
		builder.addCase(rejectJoinRequest.pending, (state) => {
			state.isRejectingJoinRequest = true;
			state.joinRequestError = "";
		});

		builder.addCase(
			rejectJoinRequest.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isRejectingJoinRequest = false;
				// Update the join request in the list
				if (state.joinRequestsApplications?.joinRequests) {
					const requestId = action.payload?.joinRequest?._id;
					const requestIndex =
						state.joinRequestsApplications.joinRequests.findIndex(
							(req) => req._id === requestId,
						);
					if (requestIndex !== -1) {
						state.joinRequestsApplications.joinRequests[requestIndex] = {
							...state.joinRequestsApplications.joinRequests[requestIndex],
							status: JoinRequestStatus.Rejected,
							deniedAt: new Date().toISOString(),
						};
					}
				}
			},
		);

		builder.addCase(
			rejectJoinRequest.rejected,
			(state, action: PayloadAction<any>) => {
				state.isRejectingJoinRequest = false;
				state.joinRequestError =
					action.payload || "Failed to reject join request";
			},
		);

		// Fetch All Add Requests (Admin)
		builder.addCase(fetchAllAddRequestsAsAdmin.pending, (state) => {
			state.isLoadingAddRequests = true;
			state.addRequestError = "";
		});

		builder.addCase(
			fetchAllAddRequestsAsAdmin.fulfilled,
			(state, action: PayloadAction<AddRequestsApplications>) => {
				state.isLoadingAddRequests = false;
				state.addRequestsApplications = action.payload;
			},
		);

		builder.addCase(
			fetchAllAddRequestsAsAdmin.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoadingAddRequests = false;
				state.addRequestError =
					action.payload || "Failed to fetch add requests";
			},
		);

		// Approve Add Request (Admin)
		builder.addCase(approveAddRequest.pending, (state) => {
			state.isApprovingAddRequest = true;
			state.addRequestError = "";
		});

		builder.addCase(
			approveAddRequest.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isApprovingAddRequest = false;
				// Update the add request in the list
				if (state.addRequestsApplications?.data) {
					const requestId = action.payload?.data?.addRequestId;
					const requestIndex = state.addRequestsApplications.data.findIndex(
						(req) => req._id === requestId,
					);
					if (requestIndex !== -1) {
						state.addRequestsApplications.data[requestIndex] = {
							...state.addRequestsApplications.data[requestIndex],
							status: AddRequestStatus.Approved,
						};
					}
				}
			},
		);

		builder.addCase(
			approveAddRequest.rejected,
			(state, action: PayloadAction<any>) => {
				state.isApprovingAddRequest = false;
				state.addRequestError =
					action.payload || "Failed to approve add request";
			},
		);

		// Reject Add Request (Admin)
		builder.addCase(rejectAddRequest.pending, (state) => {
			state.isRejectingAddRequest = true;
			state.addRequestError = "";
		});

		builder.addCase(
			rejectAddRequest.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isRejectingAddRequest = false;
				// Update the add request in the list
				if (state.addRequestsApplications?.data) {
					const requestId = action.payload?.data?.addRequestId;
					const requestIndex = state.addRequestsApplications.data.findIndex(
						(req) => req._id === requestId,
					);
					if (requestIndex !== -1) {
						state.addRequestsApplications.data[requestIndex] = {
							...state.addRequestsApplications.data[requestIndex],
							status: AddRequestStatus.Rejected,
						};
					}
				}
			},
		);

		builder.addCase(
			rejectAddRequest.rejected,
			(state, action: PayloadAction<any>) => {
				state.isRejectingAddRequest = false;
				state.addRequestError =
					action.payload || "Failed to reject add request";
			},
		);

		// ── Payout Order ──────────────────────────────────────────────────────
		builder.addCase(fetchCirclePayoutOrder.pending, (state) => {
			state.isLoadingPayoutOrder = true;
			state.payoutOrderError = "";
		});

		builder.addCase(
			fetchCirclePayoutOrder.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoadingPayoutOrder = false;
				state.circlePayoutOrder = action.payload?.data ?? action.payload;
			},
		);

		builder.addCase(
			fetchCirclePayoutOrder.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoadingPayoutOrder = false;
				state.payoutOrderError =
					action.payload || "Failed to fetch payout order";
			},
		);
	},
});

export const { setCircleData, clearJoinMessage, clearUnionCycleBalances } =
	circleSlice.actions;

export const { setAccountVerificationData } = accountVerificationSlice.actions;

export default circleSlice.reducer;
